import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  withStyles, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Typography
} from "@material-ui/core";
import Autocomplete from '@material-ui/lab/Autocomplete';
import CancelAcceptForm from "../../../../Common/CancelAcceptForm";
import t from "../../../../../languages/pl";
import { buttonStyle } from "../buttonGroupStyle";
import { compose } from 'recompose';
import withSubordinateUser from './../../../../HOC/withSubordinateUser';
import { mapCarParametersToIds } from "../../../../../requests/mapApiObject";
import { connect } from "react-redux";

const useStyles = theme => ({
  button: buttonStyle(theme),
  container: {
    margin: theme.spacing(1),
    textAlign: "center"
  },
  row: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  textfield: {
    width: 250,
    margin: theme.spacing(1)
  },
  requiredFields: {
    fontSize: 10,
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    color: "red",
  },
});

const initialState = {
  brand: { marka: '', marka_id: 0 },
  model: { model: '', model_id: 0 },
  fuel: { paliwo: '', paliwo_id: 0 },
  gearbox: { skrzynia: '', skrzynia_id: 0 },
  carType: { typ: '', typ_id: 0 },
  sap_id: '',
  mpk: ''
};

const CarParameters = props => {
  const { classes, name, data: inputData, onDialogOpen, onSubmit, nonAdminUser, possibleOptions, isPKP } = props;
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(initialState);
  const [emptyBrand, setEmptyBrand] = useState(false);
  const [emptyModel, setEmptyModel] = useState(false);
  const [emptyFuel, setEmptyFuel] = useState(false);
  const [emptyGearbox, setEmptyGearbox] = useState(false);
  const [emptyCarType, setEmptyCarType] = useState(false);
  const [emptyFuelConsumption, setEmptyFuelConsumption] = useState(false);
  const [saveReady, setSaveReady] = useState(false);

  useEffect(() => {
    let brand = possibleOptions.marki.find(option => option.marka === inputData.marka);
    let model = possibleOptions.modele.find(option => option.model === inputData.model);
    let fuel = possibleOptions.paliwa.find(option => option.paliwo === inputData.paliwo);
    let gearbox = possibleOptions.skrzynie_biegow.find(option => option.skrzynia === inputData.skrzynia);
    let carType = possibleOptions.typy_aut.find(option => option.typ === inputData.typ);
    setState({
      ...inputData,
      brand: brand ? brand : initialState.brand,
      model: model ? model : initialState.model,
      fuel: fuel ? fuel : initialState.fuel,
      gearbox: gearbox ? gearbox : initialState.gearbox,
      carType: carType ? carType : initialState.carType,
      kierowca: inputData.kierowca ? inputData.kierowca : '',
      sap_id: inputData.sap_id ? inputData.sap_id : '',
      mpk: inputData.mpk ? inputData.mpk : '',
    });
  }, [inputData]);

  useEffect(() => {
    state.brand ? setEmptyBrand(false) : setEmptyBrand(true);
  }, [state.brand, state.model, state.fuel, state.gearbox, state.carType]);

  useEffect(() => {
    state.model ? setEmptyModel(false) : setEmptyModel(true);
  }, [state.model]);

  useEffect(() => {
    state.fuel ? setEmptyFuel(false) : setEmptyFuel(true);
  }, [state.fuel]);

  useEffect(() => {
    state.gearbox ? setEmptyGearbox(false) : setEmptyGearbox(true);
  }, [state.gearbox]);

  useEffect(() => {
    state.carType ? setEmptyCarType(false) : setEmptyCarType(true);
  }, [state.carType]);

  useEffect(() => {
    state.sr_zuzycie_paliwa ? setEmptyFuelConsumption(false) : setEmptyFuelConsumption(true);
  }, [state.sr_zuzycie_paliwa]);

  const handleDialogOpen = () => {
    setOpen(true);
    onDialogOpen && onDialogOpen();
  };

  const handleSubmit = () => {
    if (!emptyBrand && !emptyModel && !emptyFuel && !emptyGearbox && !emptyCarType && !emptyFuelConsumption) {
      setSaveReady(false);
      setOpen(false);
      let data = mapCarParametersToIds(state);
      onSubmit && onSubmit(data);
    } else {
      setSaveReady(true);
    }
  };

  const handleTextChange = e => {
    e.persist();
    setState(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <React.Fragment>
      <Button
        variant="outlined"
        className={classes.button}
        onClick={handleDialogOpen}
      >
        {t.carParameters}
      </Button>

      <Dialog
        open={open}
        fullWidth={true}
        maxWidth="sm"
        onClose={() => setOpen(false)}
      >
        <DialogTitle>
          {t.carParameters} {name}
        </DialogTitle>
        <DialogContent>
          <div className={classes.container}>
            <div className={classes.row}>
              <Autocomplete
                options={possibleOptions.marki}
                getOptionLabel={option => option.marka}
                value={state.brand}
                onChange={(e, newValue) => setState(prevState => ({ ...prevState, brand: newValue, model: null }))}
                renderInput={(params) => (
                  <TextField
                    required
                    {...params}
                    className={classes.textfield}
                    label={t.brand}
                  />
                )}
              />
              <Autocomplete
                options={state.brand ? possibleOptions.modele.filter(model => model.marka_id == state.brand.marka_id) : []}
                getOptionLabel={option => option.model}
                value={state.model}
                onChange={(e, newValue) => setState(prevState => ({ ...prevState, model: newValue }))}
                renderInput={(params) => (
                  <TextField
                    required
                    {...params}
                    className={classes.textfield}
                    label={t.model}
                  />
                )}
              />
            </div>
            <div className={classes.row}>
              <Autocomplete
                options={possibleOptions.paliwa}
                getOptionLabel={option => option.paliwo}
                value={state.fuel}
                onChange={(e, newValue) => setState(prevState => ({ ...prevState, fuel: newValue }))}
                renderInput={(params) => (
                  <TextField
                    required
                    {...params}
                    className={classes.textfield}
                    label={t.fuelType}
                  />
                )}
              />
              <Autocomplete
                options={possibleOptions.skrzynie_biegow}
                getOptionLabel={option => option.skrzynia}
                value={state.gearbox}
                onChange={(e, newValue) => setState(prevState => ({ ...prevState, gearbox: newValue }))}
                renderInput={(params) => (
                  <TextField
                    required
                    {...params}
                    className={classes.textfield}
                    label={t.gearBox}
                  />
                )}
              />
            </div>
            <div className={classes.row}>
              <Autocomplete
                options={possibleOptions.typy_aut}
                getOptionLabel={option => option.typ}
                value={state.carType}
                onChange={(e, newValue) => setState(prevState => ({ ...prevState, carType: newValue }))}
                renderInput={(params) => (
                  <TextField
                    required
                    {...params}
                    className={classes.textfield}
                    label={t.type}
                  />
                )}
              />
              <TextField
                required
                label={t.expectedFuelConsumptionWithUnits}
                className={classes.textfield}
                value={state.sr_zuzycie_paliwa}
                name="sr_zuzycie_paliwa"
                type='number'
                onChange={handleTextChange}
                InputLabelProps={{
                  style: { whiteSpace: "nowrap" }
                }}
              />
            </div>
            <div className={classes.row}>
              <TextField
                label={t.description}
                className={classes.textfield}
                value={state.opis}
                name='opis'
                onChange={handleTextChange}
              />
              <TextField
                label={t.driver}
                className={classes.textfield}
                value={state.kierowca ? state.kierowca : ''}
                name='kierowca'
                onChange={handleTextChange}
              />
            </div>
            {isPKP ? <div className={classes.row}>
              <TextField
                label='SAP ID'
                className={classes.textfield}
                value={state.sap_id}
                name='sap_id'
                onChange={handleTextChange}
              />
              <TextField
                label='MPK'
                className={classes.textfield}
                value={state.mpk}
                name='mpk'
                onChange={handleTextChange}
              />
            </div> : null}
          </div>
          <Typography
            variant="body1"
            className={classes.requiredFields}
            style={{ display: saveReady ? "block" : "none" }}
          >
            *{t.reqiredFieldsNotFilled}
          </Typography>
        </DialogContent>
        <DialogActions>
          <CancelAcceptForm
            onCancel={() => setOpen(false)}
            onSubmit={handleSubmit}
            disableSubmit={nonAdminUser}
          />
        </DialogActions>
      </Dialog>

    </React.Fragment>
  );
};

CarParameters.propTypes = {
  classes: PropTypes.object,
  name: PropTypes.string,
  data: PropTypes.object,
  onDialogOpen: PropTypes.func,
  onSubmit: PropTypes.func,
  possibleOptions: PropTypes.object,
  //withSubordinateUser
  nonAdminUser: PropTypes.bool,
  isPKP: PropTypes.bool
};

const mapStateToProps = (state) => ({
  isPKP: state.authReducer.isPKP,
});

export default compose(
  withStyles(useStyles),
  withSubordinateUser,
  connect(
    mapStateToProps
  )
)(CarParameters);


